<template>
  <Panel 
    name="cruise-list" 
    :on-scroll="scrollCruiseListContainer"
    :panel-content-class="isInitialLoading ? 'overflow-hidden' : 'overflow-auto'"
  > 
    <template #panel-content>
      <div 
        id="canvas-container"
        ref="canvas-container"
        :style="isInitialLoading ? '' : `min-width: ${rowMinWidth}px;`" 
      >
        <CruiseListHeader 
          id="cruise-list-header"
          ref="cruise-list-header" 
        />
        
        <div 
          v-if="isInitialLoading"
          class="cruise-list-message position-absolute"
        >
          <PageLoader 
            :title="startupMessage" 
            class="border-0"
          />
        </div>

        <template v-else>
          <div :style="placeholderStyle" />

          <CruiseListRow
            v-for="(cruise, index) in partialCruiseList"
            :key="'cruiseItem' + (index + firstCruiseListItem )"
            :cruise="cruise"
            :reference-id="index"
            :even="((index + firstCruiseListItem )% 2) === 1"
            :highlighted="!!selectedEntryId && cruise.id === selectedEntryId"
            :row-width="rowMinWidth"
          />

          <div :style="placeholderStyleForecast" />
        </template>
      </div>
    </template>

    <template #panel-footer>
      <CruiseListFooter />
    </template>

    <template #panel-overlay>
      <Tooltips />
    </template>
  </Panel>
</template>

<script lang="ts">
import { COLUMN_WIDTHS } from '@/general/helper'
import { useAppStore } from '@/store/app'
import { useCatalogStore } from '@/store/catalog'
import { useCruiseListStore } from '@/store/cruise-list'
import { defaultPageSize, initialPageSize } from '@/store/filter/defaultValues'
import { debounce } from 'lodash-es'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import PageLoader from '../common/PageLoader.vue'
import Panel from '../common/Panel.vue'
import CruiseListFooter from './CruiseListFooter.vue'
import CruiseListHeader from './CruiseListHeader.vue'
import CruiseListRow from './CruiseListRow.vue'
import Tooltips from './Tooltips.vue'
export default defineComponent({
  components: {
    Panel,
    PageLoader,
    CruiseListHeader,
    CruiseListFooter,
    CruiseListRow,
    Tooltips,
  },

  data: () => {
    return {
      firstCruiseListItem: 0,
      lastCruiseListItem: initialPageSize,
    }
  },

  computed: {
    ...mapState(useAppStore, {
      startupMessage: store => store.startupMessage
    }),
    ...mapState(useCatalogStore, {
      totalResults: store => store.totalResults,
      loadingNextPageInProgress: store => store.loadingNextPageInProgress,
      pageNumber: store => store.pageNumber,
    }),
    ...mapState(useCruiseListStore, {
      cruiseList: store => store.cruiseList,
      selectedEntryId: store => store.selectedEntryId,
      listItemHeight: store => store.listItemHeight,
      averageLineHeight: store => store.averageLineHeight,
    }),

    isInitialLoading() {
      return !this.cruiseList.length || this.loadingNextPageInProgress && this.pageNumber === 1
    },

    partialCruiseList() {
      const start = this.firstCruiseListItem
      const end = this.lastCruiseListItem
      return this.cruiseList.slice(start, end)
    },

    rowMinWidth: () => {
      let sum = 0

      for (const key in COLUMN_WIDTHS) {
        if(!!COLUMN_WIDTHS[key]) {
          sum += COLUMN_WIDTHS[key]
        }
      }

      return sum
    },

    placeholderStyle() {
      this.resetItems()

      const listItemHeight = this.listItemHeight
      const averageLineHeight = this.averageLineHeight
      let height = 0

      for (let i = 0; i < this.firstCruiseListItem; i++) {
        height += (listItemHeight[i] > 0 ? listItemHeight[i] : averageLineHeight)
      }

      return `height: ${height}px;`
    },

    placeholderStyleForecast() {
      const height = (defaultPageSize * this.averageLineHeight) / 2
      const isCruiseListEnd = this.lastCruiseListItem >= this.totalResults 

      return `height: ${isCruiseListEnd ? 0 : height}px;`
    },
  },

  created() {
    debounce(this.checkListView, 100)
  },

  methods: {
    ...mapActions(useCatalogStore, {
      fetchNextPage: 'fetchNextPage'
    }),
    resetItems() {
      if(this.pageNumber === 1) {
        this.firstCruiseListItem = 0
        this.lastCruiseListItem = initialPageSize
      }
    },
    async checkListView (clientHeight: number, scrollTop: number) {
      const averageLineHeight = this.averageLineHeight
      const listItemHeight = this.listItemHeight
      const cruiseListResults = this.pageNumber > 1 ? this.pageNumber * defaultPageSize : initialPageSize
      const preloadPositionBefore = Math.max(0, scrollTop - Math.max(clientHeight, 1000))
      const preloadPositionAfter = (cruiseListResults - defaultPageSize) * averageLineHeight

      let sumHeightOfFormerItems = 0
      let firstItem = 0
      let lastItem = cruiseListResults

      for (let i = 0; i < listItemHeight.length; i++) {
        const currentItemHeight = listItemHeight[i] > 0 ? listItemHeight[i] : averageLineHeight
        const sumHeight = sumHeightOfFormerItems + currentItemHeight

        if (sumHeightOfFormerItems <= preloadPositionBefore && sumHeight > preloadPositionBefore) {
          firstItem = i
        }
        
        sumHeightOfFormerItems += currentItemHeight
      }

      lastItem = Math.min(lastItem, this.totalResults)
      firstItem = Math.floor(firstItem / defaultPageSize) * defaultPageSize

      if (scrollTop + clientHeight >= preloadPositionAfter && this.lastCruiseListItem < this.totalResults) {
        await this.fetchNextPage()
      }

      if (this.firstCruiseListItem !== firstItem) {
        this.firstCruiseListItem = firstItem
      }

      if (this.lastCruiseListItem !== lastItem) {
        this.lastCruiseListItem = lastItem
      }
    },

    scrollCruiseListContainer(event: any) {
      const target = event.target
      this.checkListView(target.clientHeight, target.scrollTop)
    },
  }
})
</script>

<style lang="scss" scoped>
  #cruise-list-header {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .cruise-list-message {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
</style>
